import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Download, ChevronLeft, ChevronRight, X, Share2, Copy, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
    setPlaying(!playing);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-5 py-2" data-testid="audio-player">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
            setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
          }
        }}
        onLoadedMetadata={() => audioRef.current && setDuration(audioRef.current.duration)}
        onEnded={() => setPlaying(false)}
      />
      <button onClick={toggle} data-testid="audio-play-button" className="w-14 h-14 flex items-center justify-center border border-[#D6D3CD] text-[#1C1917] hover:bg-[#F2EFE9] transition-all duration-300">
        {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
      </button>
      <div className="flex-1 space-y-1">
        <div className="w-full h-[3px] bg-[#E7E5DF] cursor-pointer rounded-full" onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const pct = (e.clientX - rect.left) / rect.width;
          if (audioRef.current) audioRef.current.currentTime = pct * audioRef.current.duration;
        }}>
          <div className="h-full bg-[#A89F91] rounded-full transition-all duration-150" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex justify-between">
          <span className="text-[11px] text-[#A89F91] font-mono">{formatTime(currentTime)}</span>
          <span className="text-[11px] text-[#A89F91] font-mono">{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}

function Lightbox({ photos, currentIndex, onClose, onPrev, onNext, fileUrl }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center animate-fade-in" data-testid="lightbox" onClick={onClose}>
      <button onClick={onClose} className="absolute top-6 right-6 text-white/60 hover:text-white z-50 transition-colors" data-testid="lightbox-close"><X size={28} /></button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-6 text-white/40 hover:text-white transition-colors" data-testid="lightbox-prev"><ChevronLeft size={40} /></button>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-6 text-white/40 hover:text-white transition-colors" data-testid="lightbox-next"><ChevronRight size={40} /></button>
      <img
        src={fileUrl(photos[currentIndex]?.storage_path)}
        alt=""
        className="max-h-[85vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-6 text-white/40 text-xs">
        {currentIndex + 1} / {photos.length}
      </div>
    </div>
  );
}

function ShareButton({ lovedOneName }) {
  const [copied, setCopied] = useState(false);
  const pageUrl = window.location.href;
  const shareText = `${lovedOneName}'s story — preserved by Heirloom Stories Studio`;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }, [pageUrl]);

  const handleNativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, url: pageUrl });
      } catch {}
    } else {
      handleCopy();
    }
  }, [shareText, pageUrl, handleCopy]);

  return (
    <div className="space-y-5">
      <p className="font-['Cormorant_Garamond'] text-lg italic text-[#78716C]">
        Share this story with family
      </p>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={handleCopy}
          data-testid="share-copy-link"
          className="inline-flex items-center gap-2 border border-[#D6D3CD] text-[#57534E] hover:text-[#1C1917] hover:border-[#A89F91] px-5 py-2.5 text-sm transition-all duration-300"
        >
          {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Link</>}
        </button>
        {typeof navigator.share === 'function' && (
          <button
            onClick={handleNativeShare}
            data-testid="share-native"
            className="inline-flex items-center gap-2 border border-[#D6D3CD] text-[#57534E] hover:text-[#1C1917] hover:border-[#A89F91] px-5 py-2.5 text-sm transition-all duration-300"
          >
            <Share2 size={14} /> Share
          </button>
        )}
      </div>
    </div>
  );
}

export default function HeirloomView({ project }) {
  const [muted, setMuted] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [heroVisible, setHeroVisible] = useState(false);
  const welcomeRef = useRef(null);

  const fileUrl = (path) => path ? `${API}/files/${path}` : '';

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="heirloom-page">
      {/* Hero Section — Full viewport, immersive */}
      <section className="relative min-h-screen flex items-end" data-testid="heirloom-hero">
        {project.hero_image ? (
          <div className="absolute inset-0">
            <img
              src={fileUrl(project.hero_image.storage_path)}
              alt={project.loved_one_name}
              className={`w-full h-full object-cover transition-opacity duration-[2000ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/40 to-[#1C1917]/10" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-[#1C1917]" />
        )}
        <div className={`relative z-10 max-w-4xl mx-auto px-6 sm:px-12 pb-20 sm:pb-28 w-full transition-all duration-[1500ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="overline text-[#A89F91]/80 mb-6 tracking-[0.25em]">Their Legacy</p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-8xl font-medium text-[#FCFBF9] leading-[1.02] mb-6" data-testid="heirloom-loved-one-name">
            {project.loved_one_name}
          </h1>
          <div className="w-16 h-[1px] bg-[#A89F91]/40 mb-6" />
          <p className="font-['Cormorant_Garamond'] text-lg sm:text-xl italic text-[#A89F91]/90 max-w-md" data-testid="heirloom-tagline">
            {project.tagline || "This is their story. Keep it close."}
          </p>
        </div>
      </section>

      {/* Welcome Video — Cinematic, central emotional moment */}
      {project.welcome_video && (
        <section className="relative" data-testid="heirloom-welcome-video-section">
          <div className="bg-[#1C1917]">
            <div className="max-w-6xl mx-auto">
              <div className="relative">
                <video
                  ref={welcomeRef}
                  src={fileUrl(project.welcome_video.storage_path)}
                  muted={muted}
                  autoPlay
                  playsInline
                  loop
                  className="w-full aspect-video object-cover"
                  data-testid="welcome-video"
                />
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#1C1917]/20 to-transparent" />
                <button
                  onClick={() => {
                    setMuted(!muted);
                    if (welcomeRef.current) welcomeRef.current.muted = !muted;
                  }}
                  data-testid="welcome-video-mute-toggle"
                  className="absolute bottom-5 right-5 bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white/80 hover:text-white p-3 transition-all duration-300"
                >
                  {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Their Story — Full Interview */}
      {project.story_video && (
        <section className="py-20 sm:py-28 bg-[#F2EFE9]" data-testid="heirloom-story-section">
          <div className="max-w-5xl mx-auto px-6 sm:px-12">
            <div className="mb-12">
              <p className="overline mb-3">Their Story</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917]">
                The Full Interview
              </h2>
            </div>
            <video
              src={fileUrl(project.story_video.storage_path)}
              controls
              playsInline
              className="w-full aspect-video bg-black"
              data-testid="story-video"
            />
          </div>
        </section>
      )}

      {/* Voice Archive */}
      {project.audio_file && (
        <section className="py-20 sm:py-28" data-testid="heirloom-voice-section">
          <div className="max-w-3xl mx-auto px-6 sm:px-12">
            <div className="mb-12">
              <p className="overline mb-3">Voice Archive</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917]">
                Hear Their Voice
              </h2>
            </div>
            <div className="border border-[#E7E5DF] p-6 sm:p-8 bg-white">
              <AudioPlayer src={fileUrl(project.audio_file.storage_path)} />
            </div>
          </div>
        </section>
      )}

      {/* Life Story Book */}
      {project.pdf_book && (
        <section className="py-20 sm:py-28 bg-[#F2EFE9]" data-testid="heirloom-book-section">
          <div className="max-w-4xl mx-auto px-6 sm:px-12">
            <div className="mb-12">
              <p className="overline mb-3">Life Story Book</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917]">
                Their Story, In Writing
              </h2>
            </div>
            <div className="bg-white border border-[#E7E5DF]">
              <div className="flex items-center justify-between p-5 border-b border-[#E7E5DF]">
                <p className="text-sm text-[#78716C]">{project.pdf_book.original_filename}</p>
                <a
                  href={fileUrl(project.pdf_book.storage_path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pdf-download"
                  className="inline-flex items-center gap-2 text-sm text-[#1C1917] border border-[#D6D3CD] hover:bg-[#F2EFE9] px-4 py-2 transition-colors"
                >
                  <Download size={14} /> Download
                </a>
              </div>
              <iframe
                src={fileUrl(project.pdf_book.storage_path)}
                className="w-full h-[600px] border-0"
                title="Life Story Book"
                data-testid="pdf-viewer"
              />
            </div>
          </div>
        </section>
      )}

      {/* Photo Gallery */}
      {project.photos && project.photos.length > 0 && (
        <section className="py-20 sm:py-28" data-testid="heirloom-gallery-section">
          <div className="max-w-6xl mx-auto px-6 sm:px-12">
            <div className="mb-12">
              <p className="overline mb-3">Photo Gallery</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917]">
                Moments Preserved
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
              {project.photos.map((photo, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  data-testid={`gallery-photo-${i}`}
                  className="aspect-[4/3] overflow-hidden group"
                >
                  <img
                    src={fileUrl(photo.storage_path)}
                    alt={`Memory ${i + 1}`}
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.03] group-hover:opacity-90"
                  />
                </button>
              ))}
            </div>
          </div>
          {lightboxIndex >= 0 && (
            <Lightbox
              photos={project.photos}
              currentIndex={lightboxIndex}
              onClose={() => setLightboxIndex(-1)}
              onPrev={() => setLightboxIndex((prev) => (prev - 1 + project.photos.length) % project.photos.length)}
              onNext={() => setLightboxIndex((prev) => (prev + 1) % project.photos.length)}
              fileUrl={fileUrl}
            />
          )}
        </section>
      )}

      {/* Life Lessons */}
      {project.life_lessons && project.life_lessons.length > 0 && (
        <section className="py-20 sm:py-28 bg-[#F2EFE9]" data-testid="heirloom-lessons-section">
          <div className="max-w-3xl mx-auto px-6 sm:px-12">
            <div className="mb-14">
              <p className="overline mb-3">Life Lessons</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917]">
                Words of Wisdom
              </h2>
            </div>
            <div className="space-y-12">
              {project.life_lessons.map((lesson, i) => (
                <div key={i} data-testid={`life-lesson-${i}`} className="border-l border-[#A89F91]/50 pl-8 sm:pl-10">
                  <p className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl italic text-[#1C1917] leading-relaxed mb-3">
                    "{lesson.text}"
                  </p>
                  {lesson.attribution && (
                    <p className="text-sm text-[#78716C]">— {lesson.attribution}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Share With Family */}
      <section className="py-16 sm:py-20" data-testid="heirloom-share-section">
        <div className="max-w-md mx-auto px-6 text-center">
          <ShareButton lovedOneName={project.loved_one_name} />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 sm:py-24 bg-[#1C1917] text-center">
        <div className="max-w-md mx-auto px-6">
          <div className="w-12 h-[1px] bg-[#A89F91]/30 mx-auto mb-8" />
          <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl italic text-[#A89F91]/80 leading-relaxed mb-8">
            "This is their story.<br />Keep it close."
          </p>
          <p className="text-[11px] text-[#78716C]/60 tracking-widest uppercase">
            Preserved by Heirloom Stories Studio
          </p>
        </div>
      </footer>
    </div>
  );
}
